/**
 * CORE-1201 — модерация участника на сцене эфира.
 *
 * У эфира ({{Lives}}, числовой id, хаб `ChatHub`) модерации не было вовсе: в
 * панели участников у каждой строки стоял один значок «убрать», а полный набор —
 * «Отозвать слово», «Заглушить», «Выключить камеру» — существовал только у
 * комнаты ({{WatchParties}}, `LiveRoomScreen`). Отчёт QA от 02.09.2026 читает
 * ровно это: «есть в Practice/Quick Room, но не перенесены в обычный Live».
 *
 * Здесь живут два решения, и оба намеренно вынесены из экранов — чтобы их можно
 * было проверить без хаба, вендора и камеры:
 *
 *  1. что показать ведущему в строке участника;
 *  2. что участник обязан сделать со своими треками.
 *
 * Источник правды — САМ РОСТЕР сцены, а не событие модерации. Причина в
 * CORE-1191: команда, живущая только в fan-out, отменяется первым же кликом
 * адресата, забывается его перезагрузкой и теряется в разрыве хаба, а хаб на
 * проде рвётся примерно раз в минуту у каждого клиента (CORE-1194). Ростер же
 * приезжает и событием `CohostsChanged`, и ответом `GET /api/lives/{id}/cohosts`.
 */

/** Строка ростера сцены в том виде, в каком её отдаёт бэкенд. */
export interface StageRosterRow {
    uid: number;
    role?: string;
    micMuted?: boolean;
    cameraDisabled?: boolean;
}

export interface StageModerationState {
    micMuted: boolean;
    cameraDisabled: boolean;
}

export const NO_STAGE_MODERATION: StageModerationState = { micMuted: false, cameraDisabled: false };

/**
 * Что сейчас запрещено ЭТОМУ участнику (по его собственному uid).
 *
 * Отсутствие строки в ростере — это не запрет. Ростер приходит пустым в
 * половине законных случаев (эфир ещё не начался, запрос не доехал, гость
 * подключился раньше первой рассылки), и трактовать пустоту как «микрофон
 * выключен» значило бы глушить человека на ровном месте — ошибка того же рода,
 * что CORE-1218, где отсутствие видеоконтейнера читалось как «камера выключена».
 */
export function resolveStageModeration(
    roster: readonly StageRosterRow[] | null | undefined,
    myUid: number | null | undefined,
): StageModerationState {
    if (!roster || myUid == null) return NO_STAGE_MODERATION;
    const row = roster.find(r => r.uid === myUid);
    if (!row) return NO_STAGE_MODERATION;
    return {
        micMuted: row.micMuted === true,
        cameraDisabled: row.cameraDisabled === true,
    };
}

export type StageAction = 'mute' | 'unmute' | 'camera-off' | 'camera-on' | 'remove';

/**
 * Набор действий в строке участника у ведущего.
 *
 * Правила:
 *  — без права модерации действий нет вовсе;
 *  — у самого ведущего (`role === 'master'`) их нет никогда: он не модерирует
 *    себя, и строка мастера в ростере флаги не несёт;
 *  — микрофон и камера предлагают ровно одно действие каждый — то, которое
 *    что-то изменит. Кнопка, которая ничего не делает, — это CORE-1191, где
 *    ведущий нажал «Отозвать слово» семь раз подряд;
 *  — «убрать со сцены» стоит последним и всегда: это тяжёлое действие, и оно не
 *    должно подменять собой лёгкие.
 */
export function stageRowActions(input: {
    canModerate: boolean;
    role?: string;
    micMuted?: boolean;
    cameraDisabled?: boolean;
}): StageAction[] {
    if (!input.canModerate) return [];
    if (input.role === 'master') return [];
    return [
        input.micMuted ? 'unmute' : 'mute',
        input.cameraDisabled ? 'camera-on' : 'camera-off',
        'remove',
    ];
}

/** Подпись действия для ведущего. */
export function stageActionLabel(action: StageAction): string {
    switch (action) {
        case 'mute': return 'Выключить микрофон';
        case 'unmute': return 'Вернуть микрофон';
        case 'camera-off': return 'Выключить камеру';
        case 'camera-on': return 'Вернуть камеру';
        case 'remove': return 'Отозвать слово';
    }
}

/** Иконка действия (Ionicons). */
export function stageActionIcon(action: StageAction): string {
    switch (action) {
        case 'mute': return 'mic-off-outline';
        case 'unmute': return 'mic-outline';
        case 'camera-off': return 'videocam-off-outline';
        case 'camera-on': return 'videocam-outline';
        case 'remove': return 'person-remove-outline';
    }
}

/**
 * Что участник видит, когда запрет приходит к нему. Пустая строка — когда
 * ничего не запрещено; экран показывает уведомление только при непустой.
 * AC4 отчёта: «Участник видит уведомление о действии ведущего».
 */
export function stageModerationNotice(state: StageModerationState): string {
    if (state.micMuted && state.cameraDisabled) return 'Ведущий выключил вам микрофон и камеру';
    if (state.micMuted) return 'Ведущий выключил вам микрофон';
    if (state.cameraDisabled) return 'Ведущий выключил вам камеру';
    return '';
}
